import type {
  HealthResponse,
  PortDaysResponse,
  PortSummary,
  ShipNow,
  ShipState,
  StatsResponse,
  TrackingHour,
} from "@cruise/core";
import { useState, type KeyboardEvent, type PointerEvent } from "react";
import { compact, minutesAgo, sinceIso, stateChip, stateLabel, thousands } from "../format";
import { palette } from "../palette";

const CHART_BAR = palette.chart.bar;
const CHART_BAR_HOVER = palette.chart.barHover;
const CHART_HOURS = 48;
const HOUR_MS = 3600_000;

/** Smallest 1/2/5 × 10^n that is >= value — a clean top tick. */
function niceCeil(value: number): number {
  const magnitude = 10 ** Math.floor(Math.log10(Math.max(1, value)));
  for (const step of [1, 2, 5, 10]) if (step * magnitude >= value) return step * magnitude;
  return 10 * magnitude;
}

/** A bar with a rounded top (radius <= 2) and a square base on the baseline. */
function barPath(x: number, y: number, w: number, h: number): string {
  const r = Math.min(2, w / 2, h);
  return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h}Z`;
}

function hourLabel(ms: number): string {
  const start = new Date(ms);
  const day = start.toLocaleDateString("en-GB", { timeZone: "UTC", day: "numeric", month: "short" });
  const h = String(start.getUTCHours()).padStart(2, "0");
  const next = String((start.getUTCHours() + 1) % 24).padStart(2, "0");
  return `${day}, ${h}:00–${next}:00 UTC`;
}

export function TrackingCard({ stats, now }: { stats: StatsResponse | null; now: number }) {
  const [active, setActive] = useState<number | null>(null);
  if (!stats) return null;

  const byHour = new Map<string, TrackingHour>(stats.hourly.map((h) => [h.hour, h]));
  const currentHourMs = Math.floor(now / HOUR_MS) * HOUR_MS;
  const slots = Array.from({ length: CHART_HOURS }, (_, i) => {
    const ms = currentHourMs - (CHART_HOURS - 1 - i) * HOUR_MS;
    return { ms, data: byHour.get(new Date(ms).toISOString().slice(0, 13)) ?? null };
  });
  const yMax = niceCeil(Math.max(1, ...slots.map((s) => s.data?.fleetShipsSeen ?? 0)));

  const W = 300;
  const H = 100;
  const left = 24;
  const top = 6;
  const bottom = 16;
  const plotW = W - left;
  const plotH = H - top - bottom;
  const slotW = plotW / CHART_HOURS;
  const barW = Math.min(24, Math.max(1, slotW - 2)); // 2px surface gap between bars

  const thisHour = slots[CHART_HOURS - 1]?.data ?? null;
  const today = stats.daily[stats.daily.length - 1];
  const finishedDays = stats.daily.filter((d) => d.complete).slice(-14).reverse();
  const shown = active === null ? null : slots[active];

  const pick = (e: PointerEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * W - left;
    setActive(x < 0 ? null : Math.min(CHART_HOURS - 1, Math.max(0, Math.floor(x / slotW))));
  };
  const step = (e: KeyboardEvent<SVGSVGElement>) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    const from = active ?? CHART_HOURS;
    setActive(Math.min(CHART_HOURS - 1, Math.max(0, from + (e.key === "ArrowLeft" ? -1 : 1))));
  };

  return (
    <div className="card glass">
      <div className="panel-h">
        <span className="label">Fleet ships heard · per hour</span>
        <span className="mono dim" style={{ fontSize: 10 }}>
          LAST 48 H
        </span>
      </div>
      <div className="body" style={{ paddingBottom: 6 }}>
        <div className="mono dim" style={{ fontSize: 10.5, lineHeight: 1.6 }}>
          This hour: <span style={{ color: "var(--text)" }}>{thisHour?.fleetShipsSeen ?? 0}</span> of{" "}
          {stats.registryShips} fleet ships · {thousands(thisHour?.vesselsSeen ?? 0)} vessels
          <br />
          Today (UTC, so far): <span style={{ color: "var(--text)" }}>{today?.fleetShipsSeen ?? 0}</span> fleet ships ·{" "}
          {thousands(today?.vesselsSeen ?? 0)} vessels
        </div>
        <div className="chart-wrap">
          <svg
            viewBox={`0 0 ${W} ${H}`}
            role="img"
            aria-label={`Bar chart: registry ships heard per hour, last ${CHART_HOURS} hours. Use left and right arrow keys to read each hour.`}
            tabIndex={0}
            onPointerMove={pick}
            onPointerLeave={() => setActive(null)}
            onKeyDown={step}
            onBlur={() => setActive(null)}
          >
            <line x1={left} x2={W} y1={top + plotH} y2={top + plotH} className="chart-axis" />
            <line x1={left} x2={W} y1={top} y2={top} className="chart-grid" />
            <text x={left - 4} y={top + 3} textAnchor="end" className="chart-tick">
              {yMax}
            </text>
            <text x={left - 4} y={top + plotH + 3} textAnchor="end" className="chart-tick">
              0
            </text>
            {slots.map((slot, i) => {
              const value = slot.data?.fleetShipsSeen ?? 0;
              if (!slot.data || value === 0) return null;
              const h = (value / yMax) * plotH;
              const x = left + i * slotW + (slotW - barW) / 2;
              return <path key={slot.ms} d={barPath(x, top + plotH - h, barW, h)} fill={i === active ? CHART_BAR_HOVER : CHART_BAR} />;
            })}
            {active !== null && (
              <rect x={left + active * slotW} y={top} width={slotW} height={plotH} className="chart-hover" />
            )}
            <text x={left} y={H - 3} className="chart-tick">
              48 h ago
            </text>
            <text x={W} y={H - 3} textAnchor="end" className="chart-tick">
              now
            </text>
          </svg>
          {shown && (
            <div
              className="chart-tip glass"
              style={{ left: `${Math.min(70, Math.max(0, ((left + (active ?? 0) * slotW) / W) * 100 - 10))}%` }}
            >
              {shown.data ? (
                <>
                  <strong>{shown.data.fleetShipsSeen} fleet ships</strong>
                  <div>
                    {thousands(shown.data.vesselsSeen)} vessels · {thousands(shown.data.positionReports)} reports
                  </div>
                </>
              ) : (
                <strong>No data</strong>
              )}
              <div className="faint">
                {hourLabel(shown.ms)}
                {active === CHART_HOURS - 1 ? " (so far)" : ""}
              </div>
              {!shown.data && <div className="faint">recorder was not running</div>}
            </div>
          )}
        </div>
        <details className="stats-table">
          <summary>Table</summary>
          <table>
            <thead>
              <tr>
                <th>Day (UTC)</th>
                <th>Fleet</th>
                <th>Vessels</th>
                <th>Hours</th>
              </tr>
            </thead>
            <tbody>
              {today && (
                <tr>
                  <td>{today.day} (so far)</td>
                  <td>{today.fleetShipsSeen}</td>
                  <td>{thousands(today.vesselsSeen)}</td>
                  <td>{today.hoursRecorded}</td>
                </tr>
              )}
              {finishedDays.map((d) => (
                <tr key={d.day}>
                  <td>{d.day}</td>
                  <td>
                    {d.fleetShipsSeen}/{d.registryShips}
                  </td>
                  <td>{thousands(d.vesselsSeen)}</td>
                  <td>{d.hoursRecorded}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <table>
            <thead>
              <tr>
                <th>Hour (UTC)</th>
                <th>Fleet</th>
                <th>Vessels</th>
                <th>Reports</th>
              </tr>
            </thead>
            <tbody>
              {[...stats.hourly].reverse().map((h) => (
                <tr key={h.hour}>
                  <td>{h.hour.replace("T", " ")}:00</td>
                  <td>{h.fleetShipsSeen}</td>
                  <td>{thousands(h.vesselsSeen)}</td>
                  <td>{thousands(h.positionReports)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      </div>
    </div>
  );
}

const STATE_ORDER: Record<ShipState, number> = {
  "under-way": 0,
  stopped: 1,
  "no-recent-signal": 2,
  "never-seen": 3,
};

export function FleetTable({
  ships,
  health,
  selectedImo,
  onSelectShip,
}: {
  ships: ShipNow[];
  health: HealthResponse | null;
  selectedImo: string | null;
  onSelectShip: (imo: string) => void;
}) {
  const rows = [...ships].sort((a, b) => STATE_ORDER[a.state] - STATE_ORDER[b.state] || a.name.localeCompare(b.name));
  return (
    <div className="card glass grow">
      <div className="panel-h">
        <span className="label">Fleet · latest AIS</span>
        <span className="mono" style={{ fontSize: 10, color: "var(--accent)" }}>
          {health ? `${health.ais.fleetShipsSeenLastHour}/${health.ais.fleetSize} SEEN LAST HOUR` : `${ships.length} VESSELS`}
        </span>
      </div>
      <div className="scroll">
        <table>
          <thead>
            <tr>
              <th>Vessel</th>
              <th>State</th>
              <th>Last AIS</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.imo} className={s.imo === selectedImo ? "selected" : undefined} onClick={() => onSelectShip(s.imo)}>
                <td>
                  {s.name}
                  <div className="faint mono" style={{ fontSize: 9.5 }}>
                    {s.nearPort ? `near ${s.nearPort.name}` : s.reportedDestination
                        ? `AIS dest ${s.reportedDestination}${s.reportedDestinationPort ? ` · ${s.reportedDestinationPort.name}` : ""}`
                        : s.line}
                  </div>
                </td>
                <td>
                  <span className={`chip ${stateChip(s.state)}`}>{stateLabel(s.state)}</span>
                </td>
                <td className="mono dim">{minutesAgo(s.minutesSinceLastPosition)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function DataHealthCard({ health, now }: { health: HealthResponse | null; now: number }) {
  if (!health) return null;
  const flagged = health.sources.filter((s) => s.state === "flagged").length;
  return (
    <div className="card glass">
      <div className="panel-h">
        <span className="label">Data health</span>
        <span className={`chip ${flagged ? "bad" : "ok"}`}>{flagged ? `${flagged} FLAGGED` : "ALL FEEDS OK"}</span>
      </div>
      <div className="statgrid">
        <div className="stat">
          <div className="label">Fleet seen 1h</div>
          <div className="v">
            {health.ais.fleetShipsSeenLastHour}
            <small>/{health.ais.fleetSize}</small>
          </div>
        </div>
        <div className="stat">
          <div className="label">Vessels 1h</div>
          <div className="v">{compact(health.ais.vesselsUpdatedLastHour)}</div>
        </div>
        <div className="stat">
          <div className="label">Last AIS</div>
          <div className="v" style={{ fontSize: 13, paddingTop: 5 }}>
            {sinceIso(health.ais.lastPositionAt, now)}
          </div>
        </div>
        <div className="stat">
          <div className="label">Archive → R2</div>
          <div className="v" style={{ fontSize: 13, paddingTop: 5 }}>
            {health.archive.lastUploadStatus === "failed" ? "FAILED " : ""}
            {sinceIso(health.archive.lastUploadAt, now)}
          </div>
        </div>
      </div>
      <div className="body">
        {health.sources.map((s) => (
          <div key={s.source} className="list-row">
            <span className="mono" style={{ fontSize: 10.5 }}>
              {s.source}
            </span>
            <span className={`chip ${s.state === "flagged" ? "bad" : s.state === "deferred" ? "dim" : "ok"}`}>
              {s.state.toUpperCase()}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function PortWeekCard({ weeks }: { weeks: PortDaysResponse[] }) {
  if (weeks.length === 0) return null;
  const totals = weeks.map((w) => ({
    name: w.port.name,
    ships: w.days.reduce((n, d) => n + d.shipsScheduled, 0),
    capacity: w.days.reduce((n, d) => n + d.knownPassengerCapacity, 0),
  }));
  const max = Math.max(1, ...totals.map((t) => t.ships));
  return (
    <div className="card glass">
      <div className="panel-h">
        <span className="label">Scheduled calls · next 7 days</span>
        <span className="mono dim" style={{ fontSize: 10 }}>
          SHIPS
        </span>
      </div>
      <div className="body">
        {totals.map((t) => (
          <div key={t.name} className="bar-row">
            <span className="nm">{t.name}</span>
            <span className="track">
              <span className="fill" style={{ width: `${(t.ships / max) * 100}%` }} />
            </span>
            <span className="num">{t.ships}</span>
          </div>
        ))}
        <p className="note" style={{ marginTop: 6 }}>
          Only ports whose schedule we can read. Capacity counts registry ships only.
        </p>
      </div>
    </div>
  );
}

export function OpsStrip({
  health,
  ports,
  now,
}: {
  health: HealthResponse | null;
  ports: PortSummary[];
  now: number;
}) {
  const scheduled = ports.filter((p) => p.hasSchedule);
  const callsToday = scheduled.reduce((n, p) => n + p.today.shipsScheduled, 0);
  const capacityToday = scheduled.reduce((n, p) => n + p.today.knownPassengerCapacity, 0);
  return (
    <div id="bottombar" className="glass">
      <div className="seg">
        <div className="k">Fleet seen · last hour</div>
        <div className="v">{health ? `${health.ais.fleetShipsSeenLastHour} / ${health.ais.fleetSize}` : "—"}</div>
      </div>
      <div className="seg">
        <div className="k">Vessels updated · last hour</div>
        <div className="v">{health ? compact(health.ais.vesselsUpdatedLastHour) : "—"}</div>
      </div>
      <div className="seg">
        <div className="k">Calls today · tracked ports</div>
        <div className="v">{callsToday}</div>
      </div>
      <div className="seg">
        <div className="k">Known capacity today</div>
        <div className="v">{compact(capacityToday)}</div>
      </div>
      <div className="seg">
        <div className="k">Last archive upload</div>
        <div className="v" style={health?.archive.lastUploadStatus === "failed" ? { color: "var(--bad)" } : undefined}>
          {health ? sinceIso(health.archive.lastUploadAt, now) : "—"}
        </div>
      </div>
    </div>
  );
}
